// Header logos for the PDF. react-pdf's <Image> only decodes PNG and JPEG, so
// every logo — the customer's white-label upload OR ClickGuard's own lockup —
// is baked into a PNG/JPEG data URI here first. Shared by the in-app download
// and the tokenised render route so both headers come out identical.
import clickguardLogoUrl from 'assets/img/cg-logo-update.svg'

export const blobToDataUri = (b: Blob) =>
	new Promise<string>((resolve, reject) => {
		const r = new FileReader()
		r.onload = () => resolve(r.result as string)
		r.onerror = reject
		r.readAsDataURL(b)
	})

// react-pdf's <Image> only decodes PNG and JPEG. Any other raster upload (WebP,
// GIF, …) has to be repainted onto a canvas and re-exported as PNG first, or it
// silently fails to render. Draw the object URL through an <img> and export PNG;
// resolve null on any failure so the header degrades to logo-less rather than
// throwing mid-download.
export const rasterizeToPng = (objectUrl: string) =>
	new Promise<string | null>((resolve) => {
		const img = new window.Image()
		img.onload = () => {
			if (!img.naturalWidth || !img.naturalHeight) {
				resolve(null)
				return
			}
			const canvas = document.createElement('canvas')
			canvas.width = img.naturalWidth
			canvas.height = img.naturalHeight
			const ctx = canvas.getContext('2d')
			if (!ctx) {
				resolve(null)
				return
			}
			ctx.drawImage(img, 0, 0)
			try {
				resolve(canvas.toDataURL('image/png'))
			} catch {
				resolve(null)
			}
		}
		img.onerror = () => resolve(null)
		img.src = objectUrl
	})

// SVG can't go through rasterizeToPng as-is: a viewBox-only file reports
// naturalWidth/Height 0 through an <img>, so the size must come from the
// markup. Resolve pixel dimensions from width/height or the viewBox, stamp
// them on the root scaled up for print (the header renders ~28pt tall; ~6x
// keeps it crisp), and paint that through the raster path. Null on any
// failure so the header degrades to logo-less.
export const svgToPngUrl = async (url: string): Promise<string | null> => {
	const text = await (await fetch(url)).text()
	const doc = new DOMParser().parseFromString(text, 'image/svg+xml')
	const svg = doc.querySelector('svg')
	if (!svg || doc.querySelector('parsererror')) return null
	let w = parseFloat(svg.getAttribute('width') ?? '')
	let h = parseFloat(svg.getAttribute('height') ?? '')
	if (!(w > 0) || !(h > 0)) {
		const vb = (svg.getAttribute('viewBox') ?? '')
			.trim()
			.split(/[\s,]+/)
			.map(Number)
		if (vb.length === 4 && vb[2] > 0 && vb[3] > 0) {
			w = vb[2]
			h = vb[3]
		}
	}
	if (!(w > 0) || !(h > 0)) return null
	const scale = Math.max(1, 168 / h)
	svg.setAttribute('width', String(Math.round(w * scale)))
	svg.setAttribute('height', String(Math.round(h * scale)))
	const blobUrl = URL.createObjectURL(
		new Blob([new XMLSerializer().serializeToString(svg)], {
			type: 'image/svg+xml'
		})
	)
	try {
		return await rasterizeToPng(blobUrl)
	} finally {
		URL.revokeObjectURL(blobUrl)
	}
}

// A blob proxied through the API may arrive untyped (the bucket stored no
// content type), and an untyped blob won't render through an <img> nor as a
// data URI react-pdf accepts. Sniff the real type from the magic bytes then.
const sniffImageType = async (blob: Blob): Promise<string> => {
	if (blob.type && blob.type !== 'application/octet-stream') return blob.type
	const head = new Uint8Array(await blob.slice(0, 64).arrayBuffer())
	if (
		head[0] === 0x89 &&
		head[1] === 0x50 &&
		head[2] === 0x4e &&
		head[3] === 0x47
	)
		return 'image/png'
	if (head[0] === 0xff && head[1] === 0xd8) return 'image/jpeg'
	const text = new TextDecoder().decode(head).trimStart().toLowerCase()
	if (text.startsWith('<svg') || text.startsWith('<?xml'))
		return 'image/svg+xml'
	return blob.type
}

/**
 * Resolve a logo held as a Blob (e.g. streamed through the API proxy) to a data
 * URI the PDF can draw: PNG/JPEG pass straight through, SVG is repainted at
 * print scale, any other raster (WebP, GIF, …) is re-exported as PNG. Null on
 * failure so the header degrades gracefully instead of aborting the download.
 */
export const blobToPdfDataUri = async (blob: Blob): Promise<string | null> => {
	try {
		const type = await sniffImageType(blob)
		const typed = blob.type === type ? blob : new Blob([blob], { type })
		if (type === 'image/png' || type === 'image/jpeg')
			return await blobToDataUri(typed)
		const objectUrl = URL.createObjectURL(typed)
		try {
			return type === 'image/svg+xml'
				? await svgToPngUrl(objectUrl)
				: await rasterizeToPng(objectUrl)
		} finally {
			URL.revokeObjectURL(objectUrl)
		}
	} catch {
		return null
	}
}

/**
 * Resolve any logo URL (object URL, data URI, bundled asset) to a data URI the
 * PDF can draw — see blobToPdfDataUri. The URL must be fetchable from the page:
 * the storage bucket that holds avatars and uploads serves no CORS headers, so
 * those go through the API proxy (ScheduledReports.whiteLabelLogoBlob) instead.
 */
export const logoToPdfDataUri = async (url: string): Promise<string | null> => {
	try {
		const blob = await (await fetch(url)).blob()
		return await blobToPdfDataUri(blob)
	} catch {
		return null
	}
}

// ClickGuard's full logo (logomark + wordmark, navy on transparent — the same
// asset the light-mode sidebar shows). Rendered in the header of every report
// that is NOT white-labelled. Rasterized once per session; a failed attempt is
// not cached so the next download retries.
let clickguardLogo: Promise<string | null> | null = null
export const clickguardLogoDataUri = (): Promise<string | null> => {
	if (!clickguardLogo) {
		clickguardLogo = logoToPdfDataUri(clickguardLogoUrl).then((uri) => {
			if (!uri) clickguardLogo = null
			return uri
		})
	}
	return clickguardLogo
}
